/**
 * ActivityRowOverflowMenu — ⋯ menu for Recent activity rows
 * (Block 6.F.5 §5.1).
 *
 * Per-row affordances kept low-density per the steward-fatigue mitigation
 * lens. The "⋯" overflow surfaces:
 *   - Open detail (jump to the resource detail page)
 *   - Archive [Name] / Restore [Name] (toggles based on isArchived)
 *
 * Archive/Restore show for a seat holding archive_providers
 * (canArchiveProviders); every other seat sees only "Open detail."
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';

export interface ActivityRowOverflowMenuProps {
  targetName: string;
  /** Whether the target resource is currently archived (is_active=false). */
  isArchived: boolean;
  /** True for a seat holding archive_providers (canArchiveProviders). */
  canArchive: boolean;
  onOpenDetail: () => void;
  onArchiveClick: () => void;
  onRestoreClick: () => void;
}

export function ActivityRowOverflowMenu({
  targetName,
  isArchived,
  canArchive,
  onOpenDetail,
  onArchiveClick,
  onRestoreClick,
}: ActivityRowOverflowMenuProps): ReactNode {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function close(): void {
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={`Actions for ${targetName}`}
        aria-expanded={open}
        aria-haspopup="menu"
        className="min-h-11 min-w-11 px-2 py-1 text-ink-soft hover:text-ink rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={`Actions menu for ${targetName}`}
          className="absolute right-0 top-full mt-1 z-30 bg-paper rounded-md shadow-lg border border-sage-800/[8%] py-1 min-w-[200px]"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onOpenDetail();
            }}
            className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-sand"
          >
            Open detail
          </button>
          {canArchive && !isArchived && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onArchiveClick();
              }}
              className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-sand"
            >
              Archive {targetName}
            </button>
          )}
          {canArchive && isArchived && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onRestoreClick();
              }}
              className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-sand"
            >
              Restore {targetName}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
